import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  SimpleChanges
} from '@angular/core';
import { HEADER_ROW_HEIGHT, RecordType, ISymbolSearchCriteria, IMdsSymbolSearchResponse } from './symbol-search.model';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { SearchResultRowDirective } from '../../directives/search-result-row.directive';
import { CdkConnectedOverlay, ConnectionPositionPair, OriginConnectionPosition, OverlayConnectionPosition, ScrollStrategy, ScrollStrategyOptions, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { DOWN_ARROW, ENTER, ESCAPE, TAB, UP_ARROW } from '@angular/cdk/keycodes';
import { SymbolRecord } from './symbol-search.model';
import { SymbolSearchService } from './symbol-search.service';
import { Subject, Subscription } from 'rxjs';
import {
  CreateEditInstructionsViewMode,
  MutualFundInvestingService
} from '../../../mutual-fund-investing/mutual-fund-investing.service';
import { IntegrationService } from '../../services/integration.service';
import { messageConstants } from '../../constants';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { HighlightPipe } from '../../pipes/highlight.pipe';

@Component({
    selector: 'symbol-search',
    templateUrl: './symbol-search.component.html',
    styleUrls: ['./symbol-search.component.scss'],
    standalone: true,
    imports: [NgClass, CdkOverlayOrigin, CdkConnectedOverlay, NgStyle, NgTemplateOutlet, SearchResultRowDirective, HighlightPipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SymbolSearchComponent implements OnInit, OnDestroy, OnChanges {
  private inputHandlerTimeout?: number;
  private clickOutsideFunc: Function;
  private touchendOutsideFunc: Function;
  private allMFSymbolSearch$: Subject<ISymbolSearchCriteria> = new Subject();
  private subscription: Subscription;
  readonly symbolRecordType = RecordType.Symbol;
  showPanel = false;
  isLoading = false;
  noFundResults = false;
  statusMessage: '';
  availableFunds: Array<SymbolRecord> = [];
  searchCriteria: ISymbolSearchCriteria;
  keyManager: ActiveDescendantKeyManager<SearchResultRowDirective>;
  scrollStrategy: ScrollStrategy;
  positions: ConnectionPositionPair[];
  @ViewChild(TemplateRef) template: TemplateRef<any>;

  @ViewChild('symbolInput') searchInput: ElementRef;
  @ViewChild('panel') panel: ElementRef;
  @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;
  filteredFunds: Array<SymbolRecord> = [];
  accountControlNumber: number;
  public deleteIcon: string;

  @ViewChildren(SearchResultRowDirective) rows: QueryList<SearchResultRowDirective>;

  width = 400;

  panelHeight = 400;

  disabled = false;

  /** The delay in milliseconds between when a keystroke occurs and when a search is performed. */
  delay = 500; // 0.5 sec delay

  @Input()
  selectedValue: string;
  @Input()
  selectedAccount: string;

  @Output()
  selected: EventEmitter<SymbolRecord> = new EventEmitter();
  @Output()
  noAvailableFunds: EventEmitter<any> = new EventEmitter();
  @Output()
  inavalidFund: EventEmitter<any> = new EventEmitter();
  minLength = 1;
  @Output()
  blankSymbolField: EventEmitter<any> = new EventEmitter();
  @Input()
  isAllMFSearch: boolean;
  @Input()
  placeholder: string;
  @Input()
  viewMode: string;

  constructor(
    private searchService: SymbolSearchService,
    private scrollStrategyOptions: ScrollStrategyOptions,
    private renderer: Renderer2,
    private integrationService: IntegrationService,
    private mfInvestingService: MutualFundInvestingService
  ) {
    this.scrollStrategy = this.scrollStrategyOptions.reposition();
    this.setOverlayPosition();
  }

  ngOnInit(): void {
    this.setSearchCriteria();
    this.clickOutsideFunc = this.renderer.listen('document', 'click', (event: any) => this.onDocumentMouseDown(event));
    this.touchendOutsideFunc = this.renderer.listen('document', 'touchend', (event: any) => this.onDocumentMouseDown(event));
    if (this.selectedAccount && !this.isAllMFSearch && this.viewMode != CreateEditInstructionsViewMode.EDIT_INSTRUCTION) {
      this.searchCriteria.accountId = this.selectedAccount;
      this.getFundsForAccount();
    }
    const assetUrl = this.integrationService.getApplicationPath();
    this.deleteIcon = assetUrl + 'assets/images/Delete.svg';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['selectedAccount'] && !changes['selectedAccount'].firstChange && !this.isAllMFSearch) {
      if (changes['selectedAccount'].currentValue !== changes['selectedAccount'].previousValue) {
        const accountNumber = changes['selectedAccount'].currentValue;
        if (accountNumber) {
          if (accountNumber.indexOf('-') > -1) {
            accountNumber.replace('-', '');
          }
          this.searchCriteria.accountId = accountNumber;
          this.getFundsForAccount();
        } else {
          this.closePanel(true);
        }
      }
    } else if (
      changes &&
      changes['viewMode'] &&
      changes['viewMode'].currentValue == 'CREATE' &&
      changes['viewMode'].previousValue == 'SUBMITTED' &&
      !this.isAllMFSearch
    ) {
      this.getFundsForAccount();
    }
  }

  private setOverlayPosition(): void {
    const originPos: OriginConnectionPosition = {
      originX: 'start',
      originY: 'bottom'
    };
    const overlayPos: OverlayConnectionPosition = {
      overlayX: 'start',
      overlayY: 'top'
    };

    const position: ConnectionPositionPair = new ConnectionPositionPair(originPos, overlayPos, 0, 0);
    this.positions = JSON.parse(JSON.stringify([position]));
  }

  private updateOverlayPosition(): void {
    setTimeout(() => {
      if (this.overlay && this.overlay.overlayRef) {
        this.overlay.offsetY = 0;
        this.overlay.overlayRef.updatePosition();
      }
    });
  }

  private getFundsForAccount(): void {
    this.isLoading = true;
    this.populateAvailableFunds();
    this.setKeyBoardInteraction();
  }

  private setKeyBoardInteraction(): void {
    setTimeout(() => {
      this.keyManager = new ActiveDescendantKeyManager<SearchResultRowDirective>(this.rows).withWrap();
      if (this.availableFunds.length === 1) {
        this.keyManager.setFirstItemActive();
      } else {
        this.keyManager.setActiveItem(-1);
      }
      this.updateOverlayPosition();
    });
  }

  private setSearchCriteria(): void {
    this.searchCriteria = { accountId: '', searchTerm: '' } as ISymbolSearchCriteria;
  }

  private setAvailableFunds(response: any): void {
    this.noFundResults = !response || response.length === 0;
    this.availableFunds = this.noFundResults ? [] : response;
    this.filteredFunds = this.noFundResults ? [] : response;
    if (this.noFundResults) {
      this.closePanel();
      this.noAvailableFunds.emit(false);
      this.accountControlNumber = null;
    } else if (!this.selectedValue && this.searchInput.nativeElement.value.length > 0) {
      this.checkIfSymbolExistsInAvailableFunds(this.selectedValue);
    } else {
      this.noAvailableFunds.emit(true);
    }
  }

  ngOnDestroy(): void {
    if (this.clickOutsideFunc) {
      this.clickOutsideFunc();
    }

    if (this.touchendOutsideFunc) {
      this.touchendOutsideFunc();
    }
  }

  handleFocus(event: any): void {
    if (!this.isAllMFSearch) {
      if ((this.availableFunds && this.availableFunds.length > 0) || this.isLoading) {
        this.openPanel();
      } else {
        this.closePanel(false);
      }
    } else {
      if (event.target.value && event.target.value.length >= this.minLength) {
        if (this.availableFunds && this.availableFunds.length) {
          this.openPanel();
        }
      } else if (event.target.value && event.target.value.length < this.minLength) {
        this.closePanel(true);
      }
    }
  }

  handleInput(event: any): void {
    const target = event.target as HTMLInputElement;
    const value: number | string | null = target.value.trim();
    if (!this.isAllMFSearch) {
      if (value && value.length >= this.minLength) {
        this.searchCriteria.accountId = value;
        const symbolData = Object.assign([], this.availableFunds);
        this.filteredFunds = this.filterSymbol(symbolData, value);
      } else {
        this.searchCriteria.accountId = '';
        this.filteredFunds = this.availableFunds.length > 0 ? this.availableFunds : [];
      }
    } else {
      this.closePanel();
      window.clearTimeout(this.inputHandlerTimeout);

      if (value && value.length >= this.minLength) {
        this.inputHandlerTimeout = window.setTimeout(() => {
          this.searchCriteria.searchTerm = value;
          this.getAllMutualFunds();
        }, 1500);
      } else if (value && value.length > 0 && value.length < this.minLength) {
        this.closePanel();
      } else {
        this.searchCriteria.searchTerm = '';
        this.filteredFunds = [];
        this.closePanel(true);
      }
    }
  }

  filterSymbol(funds: any, searchString: string): any {
    return funds.filter(
      (security) =>
        security.fundSymbol.toLowerCase().includes(searchString.toLowerCase()) ||
        security.fundName.toLowerCase().includes(searchString.toLowerCase())
    );
  }

  handleKeydown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;
    const isArrowKey = keyCode === UP_ARROW || keyCode === DOWN_ARROW;

    if (this.showPanel && (isArrowKey || keyCode === ESCAPE || keyCode === ENTER || keyCode === TAB)) {
      if (keyCode === ESCAPE || keyCode === TAB) {
        this.closePanel();
        event.preventDefault();
      } else if (isArrowKey) {
        const prevActiveItem = this.keyManager.activeItem;
        this.keyManager.onKeydown(event);
        if (isArrowKey || this.keyManager.activeItem !== prevActiveItem) {
          this.scrollToOption();
        }
      } else if (this.keyManager) {
        if (keyCode === ENTER) {
          if (this.keyManager.activeItem) {
            this.keyManager.activeItem.selectViaInteraction();
          } else if (this.isAllMFSearch && !this.keyManager.activeItem && this.filteredFunds.length > 0) {
            this.select(this.filteredFunds[0]);
          }
          event.preventDefault();
        }
      }
    }
  }

  select(record: SymbolRecord): void {
    this.clearSearch();
    this.searchInput.nativeElement.value = record.fundSymbol;
    this.selectedValue = record.fundSymbol;
    record.accountControlNumber = this.accountControlNumber;
    this.selected.emit(record);
    this.closePanel(false);
    (document.activeElement as HTMLElement).blur();
  }

  private onDocumentMouseDown = (event: any) => {
    const clickTarget = event.target as HTMLElement;
    if (
      this.overlay &&
      this.showPanel &&
      clickTarget !== this.searchInput.nativeElement &&
      !clickTarget.className.includes('deleteIcon') &&
      !this.overlay.overlayRef.overlayElement.contains(clickTarget) &&
      event.target &&
      event.target.tagName !== 'BODY'
    ) {
      this.closePanel();
    }
  };

  private openPanel(): void {
    this.showPanel = true;
  }

  clearPanelAndEmitValue(): void {
    this.showPanel = false;
    this.availableFunds = [];
    this.selected.emit(null);
  }

  closePanel(clearResults?: boolean): void {
    if (clearResults) {
      this.clearResults();
    }
    this.showPanel = false;
    if (!this.isAllMFSearch) {
      if (this.selectedValue && this.selectedValue !== '') {
        this.checkIfSymbolExistsInAvailableFunds(this.selectedValue);
      } else if (!this.selectedValue && this.searchInput.nativeElement.value.length > 0) {
        this.checkIfSymbolExistsInAvailableFunds(this.searchInput.nativeElement.value);
      }
    }
  }

  private clearResults(): void {
    this.availableFunds = [];
    this.keyManager = null;
    this.setSearchCriteria();
    this.searchInput.nativeElement.value = '';
  }

  private scrollToOption(): void {
    if (this.panel) {
      const parentElement = this.panel.nativeElement;
      const rowActive = parentElement.querySelector('.row-active');

      if (rowActive) {
        rowActive.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
      if (this.keyManager.activeItemIndex === 0) {
        this.panel.nativeElement.scrollTop = 0;
      }
    }
  }

  private getRowScrollPosition(
    rowIndex: number,
    rowHeight: number,
    currentScrollPosition: number,
    panelHeight: number
  ): number {
    const rowOffset = rowIndex > 0 ? rowIndex * rowHeight + HEADER_ROW_HEIGHT : 0;

    if (rowOffset < currentScrollPosition) {
      return rowOffset;
    }

    if (rowOffset + rowHeight > currentScrollPosition + panelHeight) {
      return Math.max(0, rowOffset - panelHeight + rowHeight);
    }

    return currentScrollPosition;
  }

  clearSearch(): void {
    this.selectedValue = '';
    this.searchInput.nativeElement.value = '';
    this.searchInput.nativeElement.focus();
    this.filteredFunds = this.availableFunds;
    if (!this.isAllMFSearch) {
      if (this.filteredFunds && this.filteredFunds.length > 0) {
        this.openPanel();
      }
    } else {
      this.closePanel(true);
    }

    this.selected.emit(null);
  }

  private checkIfSymbolExistsInAvailableFunds(symbol: string): void {
    const symbolData = this.availableFunds.find(
      (item) => item.fundSymbol === symbol || item.fundSymbol + ' - ' + item.fundName == symbol
    );
    if (symbolData) {
      if (!symbolData.status) {
        this.selected.emit({
          fundSymbol: symbolData.fundSymbol,
          fundName: symbolData.fundName,
          itemIssueId: symbolData.itemIssueId,
          accountControlNumber: this.accountControlNumber,
          status: symbolData.status,
          instructionContext: symbolData.instructionContext,
          clndAuditIntgCntlNr: symbolData.clndAuditIntgCntlNr
        });
      }
    } else {
      this.inavalidFund.emit();
    }
  }

  checkIfSymbolIsBlank(): void {
    const symbolInputValue = this.searchInput.nativeElement.value;
    if (symbolInputValue === '' && symbolInputValue.length === 0) {
      this.blankSymbolField.emit(true);
    } else if (symbolInputValue.length > 0 && this.availableFunds && this.availableFunds.length <= 0) {
      this.inavalidFund.emit();
    } else {
      this.blankSymbolField.emit(false);
    }
  }

  private populateAvailableFunds(emitSymbol?: boolean): void {
    this.searchService.getAvailableFundsForAccount(this.searchCriteria).subscribe(
      (response) => {
        this.isLoading = false;

        if (!response?.data?.attributes.hasTradeDataAccess) {
          this.inavalidFund.emit();
          this.mfInvestingService.showHideInstructionNotification({
            notificationType: 'error',
            notificationBold: messageConstants.noTradeDataAccessHeading,
            messages: [messageConstants.noTradeDataAccessMessage]
          });
        }

        this.setAvailableFunds(response?.data?.attributes.elgibleInstructions);
        this.accountControlNumber = response?.data?.attributes.accountControlNumber;
        if (this.selectedValue && this.selectedValue !== '') {
          const symbolData = Object.assign([], this.availableFunds);
          this.filteredFunds = this.filterSymbol(symbolData, this.selectedValue);
          if (emitSymbol) {
            const record: SymbolRecord = this.filteredFunds[0];
            this.select(record);
          }
        }
      },
      (error) => {
        this.isLoading = false;
        this.closePanel();
      }
    );
  }

  private async getAllMutualFunds(): Promise<void> {
    if (this.searchCriteria.searchTerm.length > 3 && this.availableFunds.length > 0) {
      this.updateOverlayPosition();
      this.openPanel();
      this.filteredFunds = this.availableFunds.filter((f) =>
        f.fundSymbol.toLowerCase().startsWith(this.searchCriteria.searchTerm.toLowerCase())
      );
    } else {
      this.isLoading = true;
      this.updateOverlayPosition();
      this.openPanel();

      this.searchService.getMdsSymbolSearchResults(this.searchCriteria).subscribe(
        (response) => {
          this.isLoading = false;
          const results = this.mapSymbolSearchResponse(response);

          if (results.length === 0) {
            this.setNoFundsFound();
          } else {
            this.setAvailableFunds(results);
            this.statusMessage = '';
            this.setKeyBoardInteraction();
            this.searchInput.nativeElement.focus();
          }
        },
        (error) => {
          this.setNoFundsFound();
        }
      );
    }
  }

  private mapSymbolSearchResponse(symbolSearchResponse: IMdsSymbolSearchResponse): any {
    let symbolSearch = [];

    symbolSearchResponse.data.forEach((item) => {
      if (!(item.symbol.length > 5)) {
        symbolSearch.push({
          fundSymbol: item.symbol,
          fundName: item.name,
        })
      }});

    return symbolSearch;
  }

  private setNoFundsFound() {
    this.availableFunds = [];
    this.filteredFunds = [];
    this.isLoading = false;
  }
}
